import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMessageSquare, FiSend, FiFilter, FiGlobe, FiCalendar, FiUser, FiClock } = FiIcons;

const Messages = () => {
  const [filter, setFilter] = useState('all');
  const [selectedConversation, setSelectedConversation] = useState(null);

  const conversations = [
    {
      id: 1,
      client: 'Sarah Johnson',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face',
      lastMessage: 'Thank you for the excellent cleaning service!',
      timestamp: '2 hours ago',
      type: 'client',
      unread: false,
      service: 'Home Cleaning'
    },
    {
      id: 2,
      client: 'Mike Chen',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
      lastMessage: 'Hi, I\'m interested in your garden maintenance service. Are you available this weekend?',
      timestamp: '4 hours ago',
      type: 'lead',
      unread: true,
      service: 'Garden Maintenance'
    },
    {
      id: 3,
      client: 'Lisa Rodriguez',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face',
      lastMessage: 'Can we reschedule tomorrow\'s appointment?',
      timestamp: '1 day ago',
      type: 'client',
      unread: false,
      service: 'Pet Sitting'
    },
    {
      id: 4,
      client: 'David Wilson',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop&crop=face',
      lastMessage: 'Hello, I need help with math tutoring for my son.',
      timestamp: '2 days ago',
      type: 'lead',
      unread: true,
      service: 'Tutoring'
    }
  ];

  const quickReplies = [
    'Thank you for your message! I\'ll get back to you shortly.',
    'I\'m available for booking. What time works best for you?',
    'I\'d be happy to help with that service. Let me check my availability.',
    'Thank you for choosing my services! I look forward to working with you.'
  ];

  const filteredConversations = conversations.filter(conv => {
    if (filter === 'all') return true;
    if (filter === 'unread') return conv.unread;
    if (filter === 'leads') return conv.type === 'lead';
    if (filter === 'clients') return conv.type === 'client';
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <motion.div 
        className="flex items-center justify-between"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Messages</h1>
          <p className="text-slate-600 mt-1">Talk to your leads and clients in one inbox</p>
        </div>
        
        <div className="flex items-center gap-4">
          <motion.button
            className="flex items-center gap-2 bg-slate-100 text-slate-600 px-4 py-2 rounded-lg hover:bg-slate-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={FiGlobe} />
            Auto-translate
          </motion.button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div 
        className="flex items-center gap-4 bg-white rounded-xl p-4 border border-slate-200"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SafeIcon icon={FiFilter} className="text-slate-500" />
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'All Messages' },
            { key: 'unread', label: 'Unread' },
            { key: 'leads', label: 'Leads' },
            { key: 'clients', label: 'Booked Clients' }
          ].map((filterType) => (
            <button
              key={filterType.key}
              onClick={() => setFilter(filterType.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                filter === filterType.key
                  ? 'bg-trust-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {filterType.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Messages Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conversations List */}
        <motion.div 
          className="lg:col-span-1 bg-white rounded-xl border border-slate-200 overflow-hidden"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="p-4 border-b border-slate-200">
            <h2 className="font-semibold text-slate-800">Conversations</h2>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {filteredConversations.map((conversation, index) => (
              <motion.div
                key={conversation.id}
                className={`p-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                  selectedConversation?.id === conversation.id ? 'bg-trust-50 border-trust-200' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                onClick={() => setSelectedConversation(conversation)}
              >
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <img 
                      src={conversation.avatar} 
                      alt={conversation.client}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    {conversation.unread && (
                      <div className="absolute -top-1 -right-1 w-3 h-3 bg-trust-500 rounded-full"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-slate-800 truncate">{conversation.client}</h3>
                      <span className="text-xs text-slate-500">{conversation.timestamp}</span>
                    </div>
                    <p className="text-sm text-slate-600 truncate mb-1">{conversation.lastMessage}</p>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        conversation.type === 'lead' 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {conversation.type === 'lead' ? 'Lead' : 'Client'}
                      </span>
                      <span className="text-xs text-slate-500">{conversation.service}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Chat Interface */}
        <motion.div 
          className="lg:col-span-2 bg-white rounded-xl border border-slate-200 overflow-hidden"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img 
                      src={selectedConversation.avatar} 
                      alt={selectedConversation.client}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <h3 className="font-semibold text-slate-800">{selectedConversation.client}</h3>
                      <p className="text-sm text-slate-600">{selectedConversation.service}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      className="flex items-center gap-2 bg-trust-500 text-white px-4 py-2 rounded-lg hover:bg-trust-600 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <SafeIcon icon={FiCalendar} />
                      Book Service
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="p-4 h-64 overflow-y-auto">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <img 
                      src={selectedConversation.avatar} 
                      alt={selectedConversation.client}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                    <div className="bg-slate-100 rounded-lg p-3 max-w-xs">
                      <p className="text-sm text-slate-800">{selectedConversation.lastMessage}</p>
                      <span className="text-xs text-slate-500 mt-1 block">
                        <SafeIcon icon={FiClock} className="inline mr-1" />
                        {selectedConversation.timestamp}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Replies */}
              <div className="p-4 border-t border-slate-200">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Quick Replies:</h4>
                <div className="flex flex-wrap gap-2 mb-4">
                  {quickReplies.map((reply, index) => (
                    <motion.button
                      key={index}
                      className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full hover:bg-slate-200 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {reply}
                    </motion.button>
                  ))}
                </div>

                {/* Message Input */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Type your message..."
                    className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trust-500"
                  />
                  <motion.button
                    className="bg-trust-500 text-white p-2 rounded-lg hover:bg-trust-600 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <SafeIcon icon={FiSend} />
                  </motion.button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-full p-8">
              <div className="text-center">
                <SafeIcon icon={FiMessageSquare} className="text-6xl text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-600 mb-2">Select a conversation</h3>
                <p className="text-slate-500">Choose a conversation from the list to start messaging</p>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Messages;